import React, { ReactNode, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import MyPageSideNavigationBar from './MyPageSideNavigationBar';
import { refreshUserInfo } from '../../hooks/useUserInfo';
import { RootState } from '../../store';

interface MyPageLayoutProps {
  children: ReactNode;
  hideFooter?: boolean;
}

const MyPageLayout: React.FC<MyPageLayoutProps> = ({ children, hideFooter = false }) => {
  const { isLoading } = useSelector((state: RootState) => state.ui);
  const { isAuthenticated, token } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  // 인증 상태 체크 및 사용자 정보 로드
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('access_token');
      console.log('MyPageLayout auth check:', { hasToken: !!accessToken, isAuthenticated });
      
      if (!accessToken && !isAuthenticated) {
        // 현재 경로를 저장하여 로그인 후 돌아올 수 있도록 함
        const currentPath = location.pathname + location.search;
        navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
        return;
      }
      
      // 토큰이 있으면 사용자 정보가 이미 로드되어 있는지 확인
      // 강제 새로고침 대신 필요한 경우에만 로드
      // if (accessToken && !userInfo) {
      //   console.log('Loading user info in MyPageLayout only if needed');
      //   try {
      //     await refreshUserInfo();
      //   } catch (error) {
      //     console.error('Failed to refresh user info:', error);
      //   }
      // }
    };

    // 컴포넌트 마운트 시 즉시 체크
    checkAuth();
  }, [isAuthenticated, token, navigate, location]);

  // 인증되지 않은 상태에서는 빈 화면 렌더링 (리다이렉트 진행 중)
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken && !isAuthenticated) {
    return (
      <LayoutContainer>
        <AuthCheckingContainer>
          <AuthCheckingSpinner />
        </AuthCheckingContainer>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <Header />
      <MainContent>
        <MyPageSideNavigationBar />
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
      {!hideFooter && <Footer />}
      
      {isLoading && (
        <LoadingOverlay>
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </LayoutContainer>
  );
};

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 320px;
  overflow-x: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  max-width: 1200px; 
  margin: 0 auto;
  position: relative;
  z-index: 1; /* stacking context 생성 */ 
  position: relative;
  padding-top: 80px; 
  padding-bottom: 128px; 
  padding-left: 242px; /* SNB 너비만큼만 */
  gap: 0;
  align-items: flex-start;
  min-height: calc(100vh - 80px);
  
  @media (max-width: 1024px) {
    padding-left: 0;
    padding-bottom: 64px;
  }
  
  @media (max-width: 768px) {
    padding-bottom: 48px;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  margin-left: 64px; /* 사이드바와 콘텐츠 사이 간격 */
  min-height: calc(100vh - 80px);
  background: white;
  
  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(92, 122, 255, 0.3);
  border-radius: 50%;
  border-top-color: #5c7aff;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AuthCheckingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: white;
`;

const AuthCheckingSpinner = styled(LoadingSpinner)`
  border-color: rgba(68, 129, 129, 0.3);
  border-top-color: #448181;
`;

export default MyPageLayout;
